import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  loadGroupData, BarCompareConstNat, WardGroupStaticMapWithBase,
  ConstituencyGroupStaticMap, getTopVI, DemographicBlock,
} from "./utils/metricsClient";
import { MPCard, loadFullMpData, fetchLatestElection } from "./utils/metricsClient";
import { getPolConfig } from "./utils/polAggregators";

/* ─── Design tokens ─────────────────────────────────────────── */
const INK        = "#0f172a";
const INK2       = "#64748b";
const INK3       = "#94a3b8";
const BORDER     = "#e2e8f0";
const STACK_TEAL = "#0abfbc";
const STACK_DARK = "#2c2c2a";
const BLUE       = "#185fa5";
const AMBER      = "#92400e";
const POS        = "#15803d";
const NEG        = "#b91c1c";
const POSBG      = "#dcfce7";
const NEGBG      = "#fee2e2";
// WASPI brand accent
const WASPI_ACCENT = "#6d28d9";   // purple

const PARTY_COLORS = {
  "Conservative": "#0087DC", "Labour": "#D50000",
  "Liberal Democrat": "#FDBB30", "Reform UK": "#12B6CF", "Reform": "#12B6CF",
  "Green": "#008066", "SNP": "#FFF95D", "Plaid Cymru": "#3F8428", "Other": "#64748b",
};
const partyCol = (label) => PARTY_COLORS[label] || STACK_TEAL;

/* ─── Micro-components ───────────────────────────────────────── */
const SL = ({ children, color = STACK_TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "4px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: STACK_DARK, whiteSpace: "nowrap" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

const Callout = ({ eyebrow, headline, sub, implication, accentColor }) => (
  <Box sx={{
    border: `1px solid ${BORDER}`,
    borderTop: `3px solid ${accentColor}`,
    borderRadius: "10px",
    p: "10px 11px",
    bgcolor: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  }}>
    <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: accentColor, mb: "4px", lineHeight: 1.1 }}>
      {eyebrow}
    </Typography>
    <Typography sx={{ fontSize: 10.5, fontWeight: 750, lineHeight: 1.22, color: INK, mb: "5px", letterSpacing: "-0.01em" }}>
      {headline}
    </Typography>
    <Typography sx={{ fontSize: 8.2, color: INK2, lineHeight: 1.32 }}>
      {sub}
    </Typography>
    {implication && (
      <Box sx={{ mt: "7px", pt: "6px", borderTop: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 8, color: accentColor, lineHeight: 1.3, fontWeight: 650 }}>
          {implication}
        </Typography>
      </Box>
    )}
  </Box>
);

const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "4px",
      bgcolor: `${col}20`, border: `0.5px solid ${col}`,
      borderRadius: "10px", px: "8px", py: "4px",
      flexShrink: 0, minWidth: 120, justifyContent: "flex-start",
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {party}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

const InlineHeader = ({ constName, areaCode, topVI, latestElection: latestElectionProp }) => {
  const [latestElectionInternal, setLatestElectionInternal] = React.useState(null);
  const latestElection = latestElectionProp !== undefined ? latestElectionProp : latestElectionInternal;
  const [winner2019, setWinner2019]         = React.useState(null);

  React.useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    // Only fetch internally if parent hasn't lifted this state up
    if (latestElectionProp === undefined) {
      fetchLatestElection(areaCode).then(d => { if (!cancelled) setLatestElectionInternal(d); }).catch(() => {});
    }
    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then(r => r.text()).then(text => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      if (lines.length < 2) return;
      const header = lines[0].replace(/^\uFEFF/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), "Plaid Cymru": Number(row.pc19),
            SNP: Number(row.snp19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce(
            (a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other"
          );
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [areaCode]);

  return (
    <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
        <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
          WASPI Report
        </Typography>
        <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "8px", mb: "3px" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label="VI Projection" party={topVI?.label} share={topVI?.constituency} />
          <SlimPill label="2024 Result"   party={latestElection?.party} share={latestElection?.share} majority={latestElection?.majority} />
          <SlimPill label="2019 Result"   party={winner2019?.party} share={winner2019?.share} />
        </Box>
      </Box>
      <Box sx={{ height: 3, width: 180, borderRadius: 4, background: `linear-gradient(90deg, ${WASPI_ACCENT}, ${WASPI_ACCENT}55)` }} />
    </Box>
  );
};

/* ─── Group config access ─────────────────────────────────────────
   Bands, option labels, colours and question text are all read from the
   aggregator, so the report can't drift from the question definition. */
const arr = (v) => [].concat(v || []).filter(Boolean);
const groupConfig = (id) => getPolConfig().groupsById?.[id] || null;

/* "More likely" and "Less likely" each combine two response options, taken from
   the group's own polarity rather than a second hard-coded key list. Don't know
   sits outside all three bands, which is why the bars are drawn against a
   shared axis instead of as a 100% stack — see the footnote below the chart. */
const voteImpactBands = () => {
  const group = groupConfig("waspi_labour_vote");
  return {
    fulltext: group?.fulltext || "",
    bands: [
      { id: "more", label: "More likely",        color: POS,  keys: arr(group?.polarity?.pos) },
      { id: "same", label: "Make no difference", color: INK3, keys: arr(group?.polarity?.neu) },
      { id: "less", label: "Less likely",        color: NEG,  keys: arr(group?.polarity?.neg) },
    ],
    dkKeys: arr(group?.polarity?.dk),
  };
};

/* Short forms of the six substantive waspi_opinion options. The aggregator
   labels are full sentences, which don't fit a bar label at this scale; the
   exact wording is carried by the question text printed above the chart. */
const OPINION_SHORT = {
  waspi_opinion_genuinely_wronged_fully: "Wronged, compensate fully",
  waspi_opinion_wronged_some:            "Wronged, some recompense",
  waspi_opinion_wronged_no_comp:         "Wronged, but no compensation",
  waspi_opinion_not_wronged:             "Not wronged at all",
  waspi_opinion_heard_no_opinion:        "Heard of WASPI, no opinion",
  waspi_opinion_not_heard:               "Never heard of WASPI",
};
const OPINION_DK_KEY = "waspi_opinion_dk";
/* "Support compensation" is a bespoke combination with no polarity block behind
   it: wronged AND owed something, at either level of generosity. Deliberately
   excludes "wronged but no compensation". */
const OPINION_SUPPORT_KEYS = [
  "waspi_opinion_genuinely_wronged_fully",
  "waspi_opinion_wronged_some",
];

const opinionConfig = () => {
  const group = groupConfig("waspi_opinion");
  return {
    fulltext: group?.fulltext || "",
    options: (group?.keys || [])
      .filter((k) => k !== OPINION_DK_KEY)
      .map((k) => ({
        key: k,
        label: OPINION_SHORT[k] || group?.labels?.[k] || k,
        color: group?.colors?.[k] || INK3,
      })),
    dkKeys: [OPINION_DK_KEY],
  };
};

// null (not 0) when the wave carries none of the band's keys, so "no data"
// stays visually distinct from a genuine zero.
const sumBand = (rows, keys, field) => {
  const vals = keys
    .map((k) => Number((rows || []).find((r) => r.key === k)?.[field]))
    .filter(Number.isFinite);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

/**
 * Horizontal bars for waspi_labour_vote: More likely / Make no difference /
 * Less likely, each showing the constituency against the national figure.
 * Pass nationalOnly for the GB variant, where there is no local comparison.
 */
const WaspiVoteImpactChart = ({ rows, constName, nationalOnly = false }) => {
  const { bands, dkKeys, fulltext } = useMemo(voteImpactBands, []);

  const data = useMemo(() => bands.map((b) => ({
    ...b,
    constituency: nationalOnly ? null : sumBand(rows, b.keys, "constituency"),
    national: sumBand(rows, b.keys, "national"),
  })), [bands, rows, nationalOnly]);

  const dkShare = sumBand(rows, dkKeys, nationalOnly ? "national" : "constituency");

  // Shared axis across both series so the three bands stay comparable. The 10%
  // floor stops a single small band from stretching to full width.
  const axisMax = Math.max(
    ...data.flatMap((d) => [d.constituency ?? 0, d.national ?? 0]), 10
  );
  const pctW = (v) => `${(((v ?? 0) / axisMax) * 100).toFixed(1)}%`;
  const fmt = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

  // flex:1 rather than height:100% — this sits above the support strip in a
  // flex column, so it takes the remaining space instead of all of it.
  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <SL color={WASPI_ACCENT}>Impact on Labour vote</SL>
      <Typography sx={{ fontSize: 8.5, fontStyle: "italic", color: INK2, mb: "8px", lineHeight: 1.4, flexShrink: 0 }}>
        “{fulltext}”
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
        {data.map((d, i) => (
          <Box key={d.id} sx={{
            display: "flex", flexDirection: "column",
            justifyContent: "center", width: "100%",
            pb: i < data.length - 1 ? "6px" : 0,
            borderBottom: i < data.length - 1 ? `0.5px solid ${BORDER}` : "none",
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: INK, mb: "5px", lineHeight: 1.15 }}>
              {d.label}
            </Typography>

            {/* National — faded, thinner. Drawn first so it reads as the backdrop.
                The bar scales inside a flex track so the value column can't be
                pushed off the edge by a full-width bar. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: nationalOnly ? 0 : "3px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: nationalOnly ? 14 : 9, width: pctW(d.national), bgcolor: nationalOnly ? d.color : `${d.color}55`, borderRadius: "2px" }} />
              </Box>
              <Typography sx={{ fontSize: nationalOnly ? 12 : 9, fontWeight: nationalOnly ? 800 : 500, color: nationalOnly ? INK : INK3, width: 40, flexShrink: 0 }}>
                {fmt(d.national)}
              </Typography>
            </Box>

            {!nationalOnly && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ height: 14, width: pctW(d.constituency), bgcolor: d.color, borderRadius: "2px" }} />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK, width: 40, flexShrink: 0 }}>
                  {fmt(d.constituency)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {!nationalOnly && (
        <Box sx={{ flexShrink: 0, display: "flex", gap: "10px", mt: "5px" }}>
          {[{ label: "GB", opacity: 0.33 }, { label: constName || "Const.", opacity: 1 }].map(({ label, opacity }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{ width: 12, height: 6, borderRadius: "1px", bgcolor: INK2, opacity }} />
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* The three bands exclude Don't know, so they are shares of all
          respondents and do not sum to 100 — state the remainder rather than
          letting the gap look like a rendering fault. */}
      <Typography sx={{ flexShrink: 0, fontSize: 8, color: INK3, mt: "5px", lineHeight: 1.3 }}>
        {dkShare != null
          ? `Excludes ${dkShare.toFixed(1)}% who said don't know.`
          : "Excludes don't know responses."}
      </Typography>
    </Box>
  );
};

/**
 * Horizontal bars for the six substantive waspi_opinion options, constituency
 * against national. Horizontal rather than vertical because the options are
 * full sentences — vertical bars at this width would need rotated or truncated
 * axis labels. Pass nationalOnly for the GB variant.
 */
const WaspiOpinionChart = ({ rows, constName, nationalOnly = false }) => {
  const { options, dkKeys, fulltext } = useMemo(opinionConfig, []);

  const data = useMemo(() => options.map((o) => ({
    ...o,
    constituency: nationalOnly ? null : sumBand(rows, [o.key], "constituency"),
    national: sumBand(rows, [o.key], "national"),
  })), [options, rows, nationalOnly]);

  const dkShare = sumBand(rows, dkKeys, nationalOnly ? "national" : "constituency");

  const axisMax = Math.max(
    ...data.flatMap((d) => [d.constituency ?? 0, d.national ?? 0]), 10
  );
  const pctW = (v) => `${(((v ?? 0) / axisMax) * 100).toFixed(1)}%`;
  const fmt = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", pr: "22px" }}>
      <SL color={WASPI_ACCENT}>Opinion of the WASPI campaign</SL>
      <Typography sx={{ fontSize: 8.5, fontStyle: "italic", color: INK2, mb: "8px", lineHeight: 1.4, flexShrink: 0 }}>
        “{fulltext}”
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
        {data.map((d) => (
          <Box key={d.key} sx={{
            display: "flex", flexDirection: "column",
            justifyContent: "center", width: "100%",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 650, color: INK, lineHeight: 1.2, mb: "3px" }}>
              {d.label}
            </Typography>
            {/* National — faded backdrop, then constituency solid on top. Bars
                scale inside a flex track so the value column stays put. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: nationalOnly ? 0 : "2.5px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: nationalOnly ? 11 : 7, width: pctW(d.national), bgcolor: nationalOnly ? d.color : `${d.color}55`, borderRadius: "2px" }} />
              </Box>
              <Typography sx={{ fontSize: nationalOnly ? 10.5 : 8.5, fontWeight: nationalOnly ? 800 : 500, color: nationalOnly ? INK : INK3, width: 38, flexShrink: 0 }}>
                {fmt(d.national)}
              </Typography>
            </Box>
            {!nationalOnly && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ height: 11, width: pctW(d.constituency), bgcolor: d.color, borderRadius: "2px" }} />
                </Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: INK, width: 38, flexShrink: 0 }}>
                  {fmt(d.constituency)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {!nationalOnly && (
        <Box sx={{ flexShrink: 0, display: "flex", gap: "10px", mt: "4px" }}>
          {[{ label: "GB", opacity: 0.33 }, { label: constName || "Const.", opacity: 1 }].map(({ label, opacity }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{ width: 12, height: 6, borderRadius: "1px", bgcolor: INK2, opacity }} />
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Typography sx={{ flexShrink: 0, fontSize: 8, color: INK3, mt: "5px", lineHeight: 1.3 }}>
        {dkShare != null
          ? `Excludes ${dkShare.toFixed(1)}% who said don't know.`
          : "Excludes don't know responses."}
      </Typography>
    </Box>
  );
};

/**
 * Combined "support compensation" share, sized to sit under the vote-impact
 * chart in the left column. Drawn against a fixed 0–100 axis (not the local max
 * the two charts above use) so the bar reads as a share of all respondents,
 * with a hairline at the 50% majority mark.
 */
const WaspiSupportStrip = ({ rows, constName, nationalOnly = false }) => {
  const con = nationalOnly ? null : sumBand(rows, OPINION_SUPPORT_KEYS, "constituency");
  const nat = sumBand(rows, OPINION_SUPPORT_KEYS, "national");
  const headline = nationalOnly ? nat : con;
  const gap = (con != null && nat != null) ? con - nat : null;
  const fmt = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

  // Name and value sit above a full-width bar rather than in a fixed label
  // column — constituency names run long ("Aberdeen South and North
  // Kincardine") and were being truncated at 62px.
  const Track = ({ value, color, label, bold }) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", mb: "3px" }}>
        <Typography sx={{
          fontSize: bold ? 9.5 : 9, fontWeight: bold ? 700 : 500,
          color: bold ? INK : INK2, flex: 1, minWidth: 0, lineHeight: 1.2,
        }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: bold ? 12 : 10, fontWeight: bold ? 800 : 600, color: bold ? INK : INK2, flexShrink: 0, lineHeight: 1.2 }}>
          {fmt(value)}
        </Typography>
      </Box>
      <Box sx={{ height: bold ? 14 : 10, bgcolor: "#eef1f5", borderRadius: "2px", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.max(Math.min(value ?? 0, 100), 0)}%`, bgcolor: color, borderRadius: "2px" }} />
       
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      flexShrink: 0, mt: "10px",
      border: `.5px solid ${POS}`,
      borderRadius: "8px", p: "10px 12px", bgcolor: "#fbfdfb",
    }}>
      <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: POS, mb: "0px" }}>
        Support compensation
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", mb: "8px" }}>
        <Typography sx={{ fontSize: 8, color: INK2, lineHeight: 1.35, flex: 1 }}>
          Wronged and owed compensation: fully compensated or some recompense combined
        </Typography>
        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 0.95, letterSpacing: "-0.02em" }}>
            {fmt(headline)}
          </Typography>
          {gap != null && (
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: gap >= 0 ? POS : NEG, lineHeight: 1.2, mt: "2px" }}>
              {`${gap >= 0 ? "+" : ""}${gap.toFixed(1)}pp vs GB`}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
        {!nationalOnly && <Track value={con} color={POS} label={constName || "Constituency"} bold />}
        <Track value={nat} color={nationalOnly ? POS : `${POS}55`} label="Great Britain" bold={nationalOnly} />
      </Box>


    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   WASPI REPORT VIEW
   Rows 1–5 match the political/holiday-tax layout; row 6 carries the
   WASPI panels.
═══════════════════════════════════════════════════════════════ */

const WaspiReportView = ({ constName, mpName, party, roles, majority, mapImages, areaCode, height = "1123px", national = false }) => {
  const [charts, setCharts] = useState({ vi: [], national: [], priorities: [], best_pm: [], loading: true });
  const topVI = getTopVI(charts.vi);
  const prevArea = useRef(null);

  // Fetched here rather than inside InlineHeader so the 2024 result is available
  // to any panel that needs it, not just the header pills.
  const [latestElection, setLatestElection] = useState(null);
  useEffect(() => {
    if (!areaCode) return;
    setLatestElection(null);
    fetchLatestElection(areaCode).then(setLatestElection).catch(() => {});
  }, [areaCode]);

  useEffect(() => {
    if (!areaCode || areaCode === prevArea.current) return;
    prevArea.current = areaCode;
    setCharts({ vi: [], national: [], priorities: [], loading: true });
    (async () => {
      try {
        const [vi, national, priorities, best_pm] = await Promise.all([
          loadGroupData({ groupId: "vote_intention",  areaCode, level: "constituency" }),
          loadGroupData({ groupId: "national_issues", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "priorities",      areaCode, level: "constituency" }),
          loadGroupData({ groupId: "best_pm",         areaCode, level: "constituency" }),
        ]);
        setCharts({ vi, national, priorities, best_pm, loading: false });
      } catch { setCharts(s => ({ ...s, loading: false })); }
    })();
  }, [areaCode]);

  const [mpData, setMpData] = useState(null);
  useEffect(() => {
    if (!constName) return;
    loadFullMpData(constName).then(setMpData);
  }, [constName]);

  /* ── Derived intelligence ───────────────────────────────────── */
  const viData = charts.vi || [];

  const topVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null, [viData]);

  const secondVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[1] || null, [viData]);

  const viGap = useMemo(() =>
    topVIParty && secondVIParty
      ? +(Number(topVIParty.constituency) - Number(secondVIParty.constituency)).toFixed(1) : null,
  [topVIParty, secondVIParty]);

  const seatRisk = useMemo(() => {
    if (viGap == null) return null;
    if (viGap > 15) return { level: "Safe",          color: POS,        bg: POSBG };
    if (viGap > 7)  return { level: "Likely",         color: STACK_TEAL, bg: "#ccfbf1" };
    if (viGap > 3)  return { level: "Marginal",       color: AMBER,      bg: "#fef3c7" };
    return               { level: "Highly marginal", color: NEG,        bg: NEGBG };
  }, [viGap]);

  const topIssues = useMemo(() =>
    (charts.national || []).filter(d => Number.isFinite(Number(d?.constituency)))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency)).slice(0, 3),
  [charts.national]);

  const topIssueAboveNat = useMemo(() =>
    (charts.national || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null,
  [charts.national]);

  const topPriorityAboveNat = useMemo(() => {
    const top3 = (charts.priorities || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Math.abs(Number(d.constituency)) > 1e-6)
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))
      .slice(0, 3);
    return top3
      .filter(d => Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null;
  }, [charts.priorities]);

  // ── WASPI panel data ─────────────────────────────────────────
  const [waspiVote, setWaspiVote] = useState([]);
  const [waspiOpinion, setWaspiOpinion] = useState([]);

  useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    (async () => {
      try {
        const [vote, opinion] = await Promise.all([
          loadGroupData({ groupId: "waspi_labour_vote", areaCode }),
          loadGroupData({ groupId: "waspi_opinion",     areaCode }),
        ]);
        if (cancelled) return;
        setWaspiVote(vote || []);
        setWaspiOpinion(opinion || []);
      } catch (e) {
        console.error("WASPI panels error", e);
        if (!cancelled) { setWaspiVote([]); setWaspiOpinion([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [areaCode]);

  /* ── National variant ────────────────────────────────────────────
     Whole-GB WASPI profile: GB constituency maps, national values,
     no MP/seat-risk, national demographics. */
  if (national) {
    const nat = (arr) => (arr || []).map((x) => ({ ...x, constituency: (x.national ?? x.constituency), national: null }));
    const viN = nat(charts.vi), issuesN = nat(charts.national), prioN = nat(charts.priorities);
    const topOf = (arr) => [...arr]
      .filter((d) => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null;
    const topVIn = topOf(viN), topIssueN = topOf(issuesN), topPrioN = topOf(prioN);
    const pct = (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : "—");

    return (
      <Box
        id="report-content"
        sx={{
          backgroundColor: "white", color: "black",
          width: "794px", height, overflow: "hidden",
          mx: "auto", p: "12px 18px",
          border: "1px solid #ccc", boxSizing: "border-box",
          display: "grid",
          gridTemplateRows: "auto 1.2fr auto auto 1.7fr",
          gridTemplateColumns: "1fr",
          gap: "7px",
        }}
      >
        {/* Header */}
        <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
            <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
              National WASPI Profile
            </Typography>
            <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.3 }}>
            {constName || "Great Britain"}
          </Typography>
          <Typography sx={{ fontSize: 10, color: INK2, mt: "1px" }}>
            {topVIn ? `${topVIn.label} leads nationally on ${pct(topVIn.constituency)} vote intention` : "WASPI sentiment & political landscape"}
          </Typography>
        </Box>

        {/* Maps + VI chart */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", minHeight: 0 }}>
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <ConstituencyGroupStaticMap groupId="past_vote" />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Past Vote by Constituency</Typography>
            </Box>
          </Box>
          <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={viN} constName="Great Britain" hideNational />
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px", borderTop: `0.5px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>National Vote Intention</Typography>
            </Box>
          </Box>
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <ConstituencyGroupStaticMap groupId="vote_intention" />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Constituency</Typography>
            </Box>
          </Box>
        </Box>

        {/* Callouts */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
          <Callout eyebrow="Vote intention" accentColor={topVIn ? partyCol(topVIn.label) : WASPI_ACCENT}
            headline={topVIn ? `${topVIn.label} leads on ${pct(topVIn.constituency)}` : "Loading…"}
            sub={topVIn ? `${topVIn.label} is the strongest party nationally on projected vote intention.` : ""}
            implication="The national baseline for benchmarking WASPI constituencies." />
          <Callout eyebrow="Issue landscape" accentColor={BLUE}
            headline={topIssueN ? `${topIssueN.label} tops the national agenda` : "Loading…"}
            sub={topIssueN ? `${topIssueN.label} is the most cited important issue across GB (${pct(topIssueN.constituency)}).` : ""}
            implication={topIssueN ? `${topIssueN.label} is the dominant national frame.` : null} />
          <Callout eyebrow="Priority signal" accentColor={WASPI_ACCENT}
            headline={topPrioN ? `${topPrioN.label} is the leading priority` : "Loading…"}
            sub={topPrioN ? `${topPrioN.label} comes through as the top local priority nationally (${pct(topPrioN.constituency)}).` : ""}
            implication={topPrioN ? `A credible national lead theme for engagement.` : null} />
        </Box>

        {/* National demographics */}
        <Box sx={{ flexShrink: 0 }}>
          <DemographicBlock national constName="Great Britain" />
        </Box>

        {/* ── National WASPI panels ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: ".95fr 1.05fr", gap: "18px", minHeight: 0, height: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", pr: "26px" }}>
            <WaspiVoteImpactChart rows={waspiVote} constName="Great Britain" nationalOnly />
            <WaspiSupportStrip rows={waspiOpinion} constName="Great Britain" nationalOnly />
          </Box>
          <WaspiOpinionChart rows={waspiOpinion} constName="Great Britain" nationalOnly />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      id="report-content"
      sx={{
        backgroundColor: "white",
        color: "black",
        width: "794px",
        height,
        overflow: "hidden",
        mx: "auto",
        p: "12px 18px",
        border: "1px solid #ccc",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateRows: "auto auto 1fr auto auto 1.7fr",
        gridTemplateColumns: "1fr",
        gap: "6px",
      }}
    >
      {/* ── ROW 1: HEADER ─────────────────────────────────────────── */}
      <InlineHeader constName={constName} areaCode={areaCode} topVI={topVI} latestElection={latestElection} />

      {/* ── ROW 2: MP CARD ────────────────────────────────────────── */}
      <Box sx={{ minHeight: 0, overflow: "hidden" }}>
        <MPCard mpData={mpData} />
      </Box>

      {/* ── ROW 3: MAPS + VI CHART ────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", minHeight: 0 }}>

        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="past_vote" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Past Vote by Ward</Typography>
          </Box>
        </Box>

        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat
              data={charts.vi}
              constName={constName}
              firstRowBadge={seatRisk && (
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: seatRisk.bg, border: `1px solid ${seatRisk.color}`,
                  borderRadius: 3, padding: "1px 6px", fontSize: 8, fontWeight: 700,
                  color: seatRisk.color, whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.5,
                }}>
                  {seatRisk.level} seat
                </span>
              )}
            />
          </Box>
          <Box sx={{ flexShrink: 0, textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px", borderTop: `0.5px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
              {`${constName || "Constituency"} Vote Intention vs National`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="vote_intention" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Ward</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── ROW 4: CALLOUTS ───────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
        <Callout
          eyebrow="Seat dynamics"
          accentColor={seatRisk?.color || WASPI_ACCENT}
          headline={(() => {
            if (!seatRisk || !topVIParty) return "Loading…";
            return `${topVIParty.label} leads in a ${seatRisk.level.toLowerCase()} seat`;
          })()}
          sub={(() => {
            if (!topVIParty || !secondVIParty) return "";
            return `${Number(topVIParty.constituency).toFixed(1)}% for ${topVIParty.label} against ${Number(secondVIParty.constituency).toFixed(1)}% for ${secondVIParty.label}${majority ? `; 2024 majority ${Number(majority).toLocaleString()}` : ""}.`;
          })()}
          implication={(() => {
            if (!seatRisk) return null;
            if (seatRisk.level === "Safe") return `Public affairs engagement can lean into policy depth rather than electoral defensiveness.`;
            if (seatRisk.level === "Likely") return `This is competitive enough that strong local evidence should still land.`;
            if (seatRisk.level === "Marginal") return `Local salience matters here, so constituency proof points will carry weight.`;
            return `Pressure is high in this seat, so campaigns need visible local relevance.`;
          })()}
        />
        <Callout
          eyebrow="Issue landscape"
          accentColor={BLUE}
          headline={(() => {
            if (!topIssueAboveNat) return topIssues[0] ? `${topIssues[0].label} leads locally` : "Loading…";
            return `${topIssueAboveNat.label} over-indexes most strongly`;
          })()}
          sub={(() => {
            if (!topIssueAboveNat) return "";
            const gap = topIssueAboveNat.gap;
            return `${Number(topIssueAboveNat.constituency).toFixed(1)}% here versus ${Number(topIssueAboveNat.national).toFixed(1)}% nationally (${gap > 0 ? "+" : ""}${gap}pp).`;
          })()}
          implication={(() => {
            if (!topIssueAboveNat) return null;
            return `${topIssueAboveNat.label} is the clearest route into the local conversation.`;
          })()}
        />
        <Callout
          eyebrow="Priority signal"
          accentColor={WASPI_ACCENT}
          headline={(() => {
            if (!topPriorityAboveNat) {
              const top = (charts.priorities || [])
                .filter(d => Number.isFinite(Number(d?.constituency)))
                .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0];
              return top ? `${top.label} comes through as the top priority` : "Loading…";
            }
            return `${topPriorityAboveNat.label} stands out in this constituency`;
          })()}
          sub={(() => {
            if (!topPriorityAboveNat) return "";
            return `${Number(topPriorityAboveNat.constituency).toFixed(1)}% locally versus ${Number(topPriorityAboveNat.national).toFixed(1)}% nationally (+${topPriorityAboveNat.gap}pp).`;
          })()}
          implication={(() => {
            if (!topPriorityAboveNat) return null;
            if (topPriorityAboveNat.gap > 8) return `This is a standout local priority and a strong lead frame for engagement.`;
            if (topPriorityAboveNat.gap > 3) return `This priority has more cut-through here than nationally.`;
            return `This priority is still credible locally, even without major divergence from the national picture.`;
          })()}
        />
      </Box>

      {/* ── ROW 5: DEMOGRAPHIC PROFILE ────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <DemographicBlock constCode={areaCode} constName={constName} />
      </Box>

      {/* ── ROW 6: WASPI PANELS ───────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: ".95fr 1.05fr", gap: "18px", minHeight: 0, height: "100%" }}>

        {/* LEFT: impact on Labour vote, with the combined support headline
            beneath it — the three bands leave slack this fills. */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", pr: "26px" }}>
          <WaspiVoteImpactChart rows={waspiVote} constName={constName} />
          <WaspiSupportStrip rows={waspiOpinion} constName={constName} />
        </Box>

        {/* RIGHT: opinion of the WASPI campaign */}
        <WaspiOpinionChart rows={waspiOpinion} constName={constName} />
      </Box>

    </Box>
  );
};

export default WaspiReportView;
